/**
 * Purchase Line Item Component
 *
 * Editable row for one line of a purchase invoice. Used inside
 * PurchaseForm's repeater. Calls back to the parent on every change.
 *
 * @module components/purchases/PurchaseLineItem
 */

import React, { useMemo } from 'react';
import { Form, Button, InputGroup } from 'react-bootstrap';
import { formatCurrency } from '../../utils/formatters';

/**
 * @param {Object} props
 * @param {Object} props.item - Line item data: { inventory_id, quantity_ordered, unit_cost, lot_number, expiration_date, notes }
 * @param {number} props.index - Position in the parent array
 * @param {Array} props.inventoryOptions - Active inventory items for the product picker
 * @param {string} [props.currency='CRC'] - Currency for the line total preview
 * @param {Function} props.onChange - (index, partialUpdates) => void
 * @param {Function} props.onRemove - (index) => void
 * @param {boolean} [props.disabled=false] - Disable all inputs
 */
const PurchaseLineItem = ({
  item,
  index,
  inventoryOptions = [],
  currency = 'CRC',
  onChange,
  onRemove,
  disabled = false,
}) => {
  const qty = Number(item.quantity_ordered || 0);
  const cost = Number(item.unit_cost || 0);
  const lineTotal = useMemo(() => qty * cost, [qty, cost]);

  const update = (field, value) => onChange && onChange(index, { [field]: value });

  return (
    <tr>
      <td style={{ minWidth: '200px' }}>
        <Form.Select
          size="sm"
          value={item.inventory_id || ''}
          onChange={(e) => update('inventory_id', e.target.value)}
          disabled={disabled}
          aria-label={`Producto linea ${index + 1}`}
        >
          <option value="">Seleccionar producto...</option>
          {inventoryOptions.map((inv) => (
            <option key={inv.id} value={inv.id}>
              {inv.sku ? `${inv.sku} - ` : ''}
              {inv.name}
              {inv.unit ? ` (${inv.unit})` : ''}
            </option>
          ))}
        </Form.Select>
      </td>
      <td style={{ width: '110px' }}>
        <Form.Control
          type="number"
          size="sm"
          min="0"
          step="0.01"
          value={item.quantity_ordered ?? ''}
          onChange={(e) => update('quantity_ordered', e.target.value === '' ? '' : Number(e.target.value))}
          disabled={disabled}
          placeholder="0"
        />
      </td>
      <td style={{ width: '140px' }}>
        <InputGroup size="sm">
          <InputGroup.Text className="px-2">{currency}</InputGroup.Text>
          <Form.Control
            type="number"
            min="0"
            step="0.01"
            value={item.unit_cost ?? ''}
            onChange={(e) => update('unit_cost', e.target.value === '' ? '' : Number(e.target.value))}
            disabled={disabled}
            placeholder="0.00"
          />
        </InputGroup>
      </td>
      <td className="text-end fw-semibold" style={{ width: '140px' }}>
        {formatCurrency(lineTotal, currency)}
      </td>
      <td style={{ width: '130px' }}>
        <Form.Control
          type="text"
          size="sm"
          value={item.lot_number || ''}
          onChange={(e) => update('lot_number', e.target.value)}
          disabled={disabled}
          placeholder="Opcional"
          maxLength={50}
        />
      </td>
      <td style={{ width: '150px' }}>
        <Form.Control
          type="date"
          size="sm"
          value={item.expiration_date || ''}
          onChange={(e) => update('expiration_date', e.target.value)}
          disabled={disabled}
        />
      </td>
      <td style={{ minWidth: '150px' }}>
        <Form.Control
          type="text"
          size="sm"
          value={item.notes || ''}
          onChange={(e) => update('notes', e.target.value)}
          disabled={disabled}
          placeholder="Opcional"
          maxLength={200}
        />
      </td>
      <td className="text-end" style={{ width: '50px' }}>
        <Button
          variant="link"
          size="sm"
          className="text-danger p-1"
          onClick={() => onRemove && onRemove(index)}
          disabled={disabled}
          title="Eliminar linea"
        >
          <i className="bi bi-trash"></i>
        </Button>
      </td>
    </tr>
  );
};

export default PurchaseLineItem;
